import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  Tabs,
  Tab,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { styled, keyframes } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import StarIcon from '@mui/icons-material/Star';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SecurityIcon from '@mui/icons-material/Security';
import { Helmet } from 'react-helmet';

// Components
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';

// Animations (matched from Resources page)
const fadeInUp = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(60px) scale(0.98); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0) scale(1); 
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  25% { transform: translateY(-15px) rotate(2deg); }
  50% { transform: translateY(0px) rotate(0deg); }
  75% { transform: translateY(-8px) rotate(-1deg); }
`;

const FloatingElement = styled(Box)<{ delay?: number }>(({ delay = 0 }) => ({
  position: 'absolute',
  opacity: 0.04,
  animation: `${float} 12s ease-in-out ${delay}s infinite`,
  pointerEvents: 'none',
  color: '#2A9235',
}));

// Page wrapper to fix footer positioning
const PageWrapper = styled(Box)({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  // Match Resources page background
  background: `
    radial-gradient(circle at 15% 15%, rgba(42, 146, 53, 0.12) 0%, transparent 60%),
    radial-gradient(circle at 85% 85%, rgba(76, 175, 80, 0.08) 0%, transparent 60%),
    radial-gradient(circle at 50% 20%, rgba(139, 195, 74, 0.06) 0%, transparent 70%),
    radial-gradient(circle at 20% 80%, rgba(102, 187, 106, 0.05) 0%, transparent 60%),
    linear-gradient(135deg, #fafafa 0%, #f0f4f8 100%)
  `,
  position: 'relative',
  overflow: 'hidden',
});

// Main content container that grows to fill space
const MainContainer = styled(Container)(({ theme }) => ({
  flex: 1,
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(4),
  [theme.breakpoints.up('md')]: {
    paddingTop: theme.spacing(10),
  },
}));

const ResponsiveTabsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  maxWidth: '500px',
  margin: '0 auto',
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    maxWidth: '350px',
  },
}));

const ResponsiveStyledTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: 'rgba(255,255,255,0.95)',
  borderRadius: 999,
  padding: theme.spacing(0.5),
  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(42,146,53,0.08)' : 'rgba(255,255,255,0.06)'}`,
  boxShadow: '0 6px 18px rgba(16,24,40,0.06)',
  minHeight: 48,
  width: 'auto',
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTabs-flexContainer': {
    alignItems: 'center',
  }
}));

const ResponsiveStyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontSize: '0.95rem',
  fontWeight: 600,
  color: '#4b5563',
  flex: '0 1 auto',
  minWidth: 140,
  maxWidth: 260,
  padding: theme.spacing(0.75, 3),
  borderRadius: 999,
  margin: theme.spacing(0, 0.5),
  whiteSpace: 'nowrap',
  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  minHeight: 48,

  [theme.breakpoints.down('sm')]: {
    fontSize: '0.85rem',
    minWidth: 110,
    padding: theme.spacing(0.5, 2),
    gap: theme.spacing(0.75),
  },

  '& .MuiSvgIcon-root': {
    fontSize: '1.05rem'
  },

  '&.Mui-selected': {
    color: '#ffffff',
    background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 30px rgba(42,146,53,0.12)',
  },

  '&:hover:not(.Mui-selected)': {
    backgroundColor: 'rgba(42,146,53,0.04)',
    transform: 'translateY(-1px)',
  }
}));

// Keep original typography styling
const MainTitle = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(1.5rem, 3.5vw, 2.2rem)',
  fontWeight: 600,
  color: '#000000',
  textAlign: 'center',
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(3),
  letterSpacing: '-0.01em',
  lineHeight: 1.2,
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(0.9rem, 1.8vw, 1rem)',
  fontWeight: 400,
  color: '#6b7280',
  textAlign: 'center',
  lineHeight: 1.6,
  maxWidth: '560px',
  margin: '0 auto',
  marginTop: theme.spacing(0.5),
  opacity: 1,
}));

const UnifiedPricingCard = styled(Paper)(({ theme }) => ({
  padding: 0,
  height: '100%',
  minHeight: 'auto',
  [theme.breakpoints.up('md')]: {
    minHeight: 360,
  },
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 20,
  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(249, 250, 251, 0.95) 100%)',
  border: '2px solid rgba(42, 146, 53, 0.12)',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12), 0 10px 25px rgba(42, 146, 53, 0.15), 0 5px 15px rgba(0, 0, 0, 0.08)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: `${fadeInUp} 0.9s ease-out both`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 6,
    background: 'linear-gradient(90deg, #2A9235 0%, #4CAF50 30%, #66BB6A 60%, #81C784 100%)',
  },
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: '0 30px 60px rgba(42, 146, 53, 0.2), 0 20px 40px rgba(0, 0, 0, 0.15), 0 10px 25px rgba(42, 146, 53, 0.1)',
  }
}));

const UnifiedCardHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5, 2.5, 1.5, 2.5),
  textAlign: 'center',
  position: 'relative',
  backgroundColor: 'rgba(42, 146, 53, 0.04)',
  minHeight: 'auto',
  [theme.breakpoints.up('md')]: {
    minHeight: '100px',
  },
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const CardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2.5, 1.5, 2.5),
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 'auto',
  [theme.breakpoints.up('md')]: {
    minHeight: '160px',
  },
}));

const CardActions = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2.5, 2.5, 2.5),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  // push actions to the bottom of the card so multiple cards align their buttons
  marginTop: 'auto',
  minHeight: '72px',
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(1.25, 3),
  fontSize: '1rem',
  fontWeight: 700,
  textTransform: 'none',
  minWidth: '220px',
  minHeight: '48px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
  color: 'white',
  border: 'none',
  boxShadow: '0 6px 18px rgba(42, 146, 53, 0.18)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.16), transparent)',
    transition: 'left 0.5s ease',
  },
  '&:hover': {
    background: 'linear-gradient(135deg, #1e7a2b 0%, #43a047 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 30px rgba(42, 146, 53, 0.22)',
    '&::before': {
      left: '100%',
    }
  }
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(1.5),
  padding: theme.spacing(0.5),
  borderRadius: theme.spacing(0.5),
  transition: 'all 0.2s ease',
  minHeight: '32px',
  '&:hover': {
    backgroundColor: 'rgba(42, 146, 53, 0.04)',
    transform: 'translateX(2px)',
  }
}));

const ContactCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1),
  backgroundColor: 'rgba(42, 146, 53, 0.08)',
  border: '1px solid rgba(42, 146, 53, 0.2)',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  marginTop: theme.spacing(1),
  '&:hover': {
    backgroundColor: 'rgba(42, 146, 53, 0.12)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(42, 146, 53, 0.15)',
  }
}));

const Pricing: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleTrialClick = () => {
    navigate('/signup-individual');
  };

  const handlePremiumClick = () => {
    navigate('/subscription');
  };

  const handleSchoolTrialClick = () => {
    navigate('/signup-school-select');
  };

  const handleQuoteClick = () => {
    navigate('/quote-request');
  };

  return (
    <><PageWrapper>
      <Helmet>
        <title>Pricing Plans - Spelling Hero</title>
      </Helmet>

      <Navbar currentPage="pricing" />

      <MainContainer maxWidth="lg">
        {/* Floating Background Elements (to match Resources) */}
        <FloatingElement sx={{ top: '8%', left: '3%' }} delay={0}>
          <SchoolIcon sx={{ fontSize: 120 }} />
        </FloatingElement>
        <FloatingElement sx={{ top: '16%', right: '6%' }} delay={3}>
          <StarIcon sx={{ fontSize: 110 }} />
        </FloatingElement>
        <FloatingElement sx={{ bottom: '18%', left: '10%' }} delay={6}>
          <SecurityIcon sx={{ fontSize: 120 }} />
        </FloatingElement>
        <FloatingElement sx={{ bottom: '10%', right: '10%' }} delay={9}>
          <TrendingUpIcon sx={{ fontSize: 100 }} />
        </FloatingElement>

        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <MainTitle variant="h2">
            Simple, Transparent Plans for Everyone
          </MainTitle>
          <SubTitle variant="h5">
            Choose the perfect plan for your spelling journey. Start with our free trial or unlock premium features.
          </SubTitle>
        </Box>

        {/* Tab Selection - Fixed responsive container */}
        <ResponsiveTabsContainer>
          <ResponsiveStyledTabs
            value={currentTab}
            onChange={handleTabChange}
            centered
          >
            <ResponsiveStyledTab
              icon={<SchoolIcon />}
              iconPosition="start"
              label="For Schools" />
            <ResponsiveStyledTab
              icon={<PersonIcon />}
              iconPosition="start"
              label="For Individuals" />
          </ResponsiveStyledTabs>
        </ResponsiveTabsContainer>

        {/* School Plans */}
        {currentTab === 0 && (
          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            <Typography
              variant="h4"
              align="center"
              sx={{
                mb: 2.5,
                fontWeight: 600,
                color: 'text.primary',
                fontSize: 'clamp(1.2rem, 2vw, 1.5rem)'
              }}
            >
              Plans for Teachers, Schools & Districts
            </Typography>

            <Grid container spacing={2} justifyContent="center" alignItems="stretch">
              <Grid item xs={12} md={6}>
                <UnifiedPricingCard elevation={2}>
                  <UnifiedCardHeader>
                    <Typography variant="h5" fontWeight={700} color="text.primary">
                      Start Your 30-Day School Pilot
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
                      Full Access for Evaluation
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Test drive with your entire school
                    </Typography>
                  </UnifiedCardHeader>

                  <CardContent>
                    {[
                      'Full access for all teachers and students',
                      'Test with both SSO and Anonymous Username setups',
                      'No obligation, no credit card required',
                      'Perfect for evaluating Spelling Hero for your needs'
                    ].map((feature, index) => (
                      <FeatureItem key={index}>
                        <CheckCircleIcon sx={{ color: '#2A9235', mr: 2 }} />
                        <Typography variant="body2" fontWeight={500}>
                          {feature}
                        </Typography>
                      </FeatureItem>
                    ))}
                  </CardContent>

                  <CardActions>
                    <PrimaryButton fullWidth onClick={handleSchoolTrialClick}>
                      Start Free 30-Day School Pilot
                    </PrimaryButton>
                  </CardActions>
                </UnifiedPricingCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <UnifiedPricingCard elevation={2}>
                  <UnifiedCardHeader>
                    <Typography variant="h5" fontWeight={700} color="text.primary">
                      Post-Pilot & Custom Quotes
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
                      Tailored Pricing Solutions
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Scalable plans for any organization
                    </Typography>
                  </UnifiedCardHeader>

                  <CardContent>
                    {[
                      'Flexible Plan as per your needs',
                      'Bulk Student Licenses',
                      'Teacher Licenses free',
                      'Volume discounts available',
                      'We accept Purchase Orders',
                    ].map((feature, index) => (
                      <FeatureItem key={index}>
                        <CheckCircleIcon sx={{ color: '#2A9235', mr: 2 }} />
                        <Typography variant="body2" fontWeight={500}>
                          {feature}
                        </Typography>
                      </FeatureItem>
                    ))}

                    {/* Contact card kept inside CardContent so it does not increase CardActions height */}
                    <Box sx={{ mt: 2 }}>
                      <ContactCard onClick={() => window.location.href = 'mailto:sales@pragnatyx.com'}>
                        <EmailIcon sx={{ mr: 1, color: '#2A9235' }} />
                        <Typography variant="body2" fontWeight={600} color="text.primary">
                           Contact us at sales@pragnatyx.com
                        </Typography>
                      </ContactCard>
                    </Box>

                  </CardContent>

                  <CardActions>
                    <PrimaryButton fullWidth onClick={handleQuoteClick}>
                      Request a Quote
                    </PrimaryButton>
                  </CardActions>
                </UnifiedPricingCard>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Individual Plans */}
        {currentTab === 1 && (
          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            <Typography
              variant="h4"
              align="center"
              sx={{
                mb: 3,
                fontWeight: 600,
                color: 'text.primary',
                fontSize: 'clamp(1.2rem, 2vw, 1.5rem)'
              }}
            >
              Plans for Parents & Individuals
            </Typography>

            <Grid container spacing={3} justifyContent="center" alignItems="stretch">
              <Grid item xs={12} md={6}>
                <UnifiedPricingCard elevation={2}>
                  <UnifiedCardHeader>
                    <Typography variant="h5" fontWeight={700} color="text.primary">
                      Start with a Free Trial
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
                      7-Day Full Access
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Experience everything, risk-free
                    </Typography>
                  </UnifiedCardHeader>

                  <CardContent>
                    {[
                      { text: 'All Premium Features', icon: <CheckCircleIcon sx={{ color: '#2A9235', mr: 2 }} /> },
                      { text: 'Unlimited Practice', icon: <CheckCircleIcon sx={{ color: '#2A9235', mr: 2 }} /> },
                      { text: 'No Credit Card Required', icon: <CheckCircleIcon sx={{ color: '#2A9235', mr: 2 }} /> }
                    ].map((feature, index) => (
                      <FeatureItem key={index}>
                        {feature.icon}
                        <Typography variant="body2" fontWeight={500}>
                          {feature.text}
                        </Typography>
                      </FeatureItem>
                    ))}
                  </CardContent>

                  <CardActions>
                    <PrimaryButton fullWidth onClick={handleTrialClick}>
                      Start 7-Day Free Trial
                    </PrimaryButton>
                  </CardActions>
                </UnifiedPricingCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <UnifiedPricingCard elevation={2}>
                  <UnifiedCardHeader>
                    <Typography variant="h5" fontWeight={700} color="text.primary">
                      Unlock Premium Membership
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
                      Uninterrupted Learning
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Flexible plans with great savings
                    </Typography>
                  </UnifiedCardHeader>

                  <CardContent>
                    {[
                      'Monthly Plan',
                      'Quarterly Plan (Save 10%)',
                      'Yearly Plan (Save 20%)'
                    ].map((feature, index) => (
                      <FeatureItem key={index}>
                        <CheckCircleIcon sx={{ color: '#2A9235', mr: 2 }} />
                        <Typography variant="body2" fontWeight={500}>
                          {feature}
                        </Typography>
                      </FeatureItem>
                    ))}
                  </CardContent>

                  <CardActions>
                    <PrimaryButton fullWidth onClick={handlePremiumClick}>
                      View Membership Plans
                    </PrimaryButton>
                  </CardActions>
                </UnifiedPricingCard>
              </Grid>
            </Grid>
          </Box>
        )}
      </MainContainer>
    </PageWrapper>
    <Footer />
    </>
  );
};

export default Pricing;
